import { type EntitySubset } from '../entity-subset.js'
import { type Entity } from '../entity.js'
import {
	type ExecutionContext,
	type ExecutionLocation,
} from '../../script/execution-context.js'
import type {
	EntityClass,
	EntityCommon,
	StaticEntityCommon,
} from '../../types.js'
import { BoInfoSymbol } from '../../consts.js'
import { type EntityMethod } from '../entity-method.js'
import { type BoReference } from '../bo-reference.js'
import { type Parameter } from '../../script/parameter.js'
import { type TypeReference } from '../../data/type-reference.js'
import { type EntityProperty } from '../entity-property.js'
import { type StaticEntity } from '../static-entity.js'
import { type PropertyChain } from '../../data/data-query-filter.js'
import { type BusinessObject } from '../business-object.js'
export function getNestedPropertyInfo(
	obj: EntityCommon,
	path: string,
	allClasses: Record<string, Record<string, any>>,
): PropertyChain | null {
	const parts = path.split('.')
	let chain: string[] = []
	let propertyType = ''
	let propertyKindType: 'primitive' | 'entity' | 'staticEntity' = 'primitive'

	for (const [idx, part] of parts.entries()) {
		const availableProperties = obj[BoInfoSymbol].allPropertyNames
		if (obj[BoInfoSymbol].staticEntityProperties.includes(part)) {
			propertyKindType = 'staticEntity'
		}
		let foundProperty = findNearest(part, availableProperties)
		if (!foundProperty) {
			foundProperty = part
		} else {
			const combinedProperty = parts.slice(idx, idx + 2).join('.') // things like 'gender.id'
			foundProperty = findNearest(combinedProperty, availableProperties)
		}

		if (foundProperty) {
			chain.push(foundProperty)
			propertyType = obj[BoInfoSymbol].propertyTypes[foundProperty]
			obj = obj[foundProperty as keyof EntityCommon] as unknown as EntityCommon
			break
		} else {
			const availableRelations = {
				// ...obj?.[BoInfoSymbol]?.oneToManyRelationships, // don't use one-to-many here!
				...obj?.[BoInfoSymbol]?.manyToOneRelationships,
				// TODO: add oneToOne when implemented
			}
			const foundRelation = findNearest(part, Object.keys(availableRelations))
			const relationType = foundRelation
				? availableRelations[foundRelation]?.()?.__baseType
				: null
			if (relationType && foundRelation) {
				chain.push(foundRelation)
				const [moduleId, boId] = relationType.split('.')
				obj = allClasses[moduleId]?.[boId]
				propertyKindType = 'entity'
			} else if ((obj as any)?.[BoInfoSymbol]?.boType == 'StaticEntity') {
				propertyType = (obj as unknown as StaticEntityCommon).__type
				propertyKindType = 'staticEntity'
				break
			} else if (
				(obj as EntityClass)?.[BoInfoSymbol]?.embeddedEntities?.[parts[0]]
			) {
				chain = parts
				propertyType = (obj as unknown as EntityCommon).__type
				propertyKindType = 'entity'
				break
			}
		}
	}

	if (!chain.length) return null
	if (chain.length < parts.length) {
		chain.push(parts.slice(chain.length).join('.')) // add remaining "unmatched" parts (e.g. ['gender', 'id']) as a final property joined by dots ('gender.id')
	}
	return {
		chain,
		propertyType,
		propertyKindType,
	}

	function findNearest(name: string, names: readonly string[]) {
		if (names.includes(name)) return name
		const nearest = names.find((n) => n.toLowerCase() == name.toLowerCase())
		return nearest
	}
}
